import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { trackEvent } from '../lib/clarity';
import { getPendingEmail, getPendingPassword, clearPendingRegistration } from '../lib/pendingRegistration';
import { verifyEmailCode, resendVerificationCode } from '../services/emailVerificationService';
// Card, logo, button, input, error and info styling are shared with the rest of the auth
// pages - see the note in ResetPasswordPage. Only the six-box code input is new (below).
import styles from './LoginPage.module.css';
import own from './VerifyEmailPage.module.css';

const CODE_LENGTH = 6;

// Mirrors emailVerificationService.TTL_MINUTES on the backend. Only a fallback: the real
// value rides along on the registration and resend responses, because a client-side
// constant that drifts from the server's would show a countdown that lies.
const FALLBACK_TTL_MINUTES = 10;

// How long the resend button stays disabled after a send. Shorter than the code's life on
// purpose - a user whose email is slow should be able to ask again without waiting out the
// full ten minutes - but long enough that a frustrated double-click does not burn two of
// the five codes their address is allowed in an hour.
const RESEND_COOLDOWN_MS = 45 * 1000;

// The registration form's rule, and the backend's. The password entered on this page
// becomes the account's password, so it is held to the same floor.
const PASSWORD_MIN_LENGTH = 8;

function mmss(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

function normalise(email) {
  return String(email || '').trim().toLowerCase();
}

/**
 * How the user got here, which decides what the page may truthfully say on arrival.
 *
 *   sent       the registration form just mailed a code - there is a countdown to show
 *   throttled  the hourly cap stopped a new code; the newest one in the inbox still works
 *   failed     the account was created but the mail did not go out
 *   cold       anything else - a redirect from /login or a route guard, or a direct visit.
 *              No code was sent on the way here, so the page must not claim one was, must
 *              not invent a countdown for it, and must not make the user wait to ask.
 */
function arrivalFrom(state) {
  if (!state) return 'cold';
  if (state.codeSent === true) return 'sent';
  if (state.throttled === true) return 'throttled';
  if (state.codeSent === false) return 'failed';
  return 'cold';
}

function initialNotice(arrival, expiresAt) {
  if (arrival === 'failed') {
    return { error: 'Your account was created, but we could not send the code. Use "Resend code" to try again.', info: '' };
  }
  if (arrival === 'throttled') {
    return expiresAt
      ? { error: '', info: "We've already sent several codes to this address, so we didn't send another. Use the most recent one in your inbox." }
      : { error: "We've sent several codes to this address in the last hour, and the most recent has expired. Please wait a while, then request a new one.", info: '' };
  }
  return { error: '', info: '' };
}

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, signOut } = useAuth();

  // Captured once. Everything the page says about the code it is waiting for is decided by
  // how the user arrived; a later navigate() that refreshes the history state (see resend)
  // must not re-derive it underneath the rendered page.
  const [initial] = useState(() => {
    const state = location.state || {};
    const arrival = arrivalFrom(location.state);
    const sentAt = typeof state.sentAt === 'number' ? state.sentAt : null;
    let expiresAt = typeof state.expiresAt === 'number' ? state.expiresAt : null;
    if (arrival === 'sent' && expiresAt === null && sentAt !== null) {
      expiresAt = sentAt + FALLBACK_TTL_MINUTES * 60 * 1000;
    }
    return { state, arrival, sentAt, expiresAt, email: normalise(state.email || getPendingEmail()) };
  });

  // Where the address comes from, in order: the navigation that sent us here, then the
  // sessionStorage copy (a refresh), then the signed-in-but-unverified session (someone
  // bounced out of /app by ProtectedRoute). Latched once known, so signing a stale session
  // out while finishing cannot empty it mid-flow. If all three are empty we cannot ask for a
  // code at all, and the page says so rather than showing a form that cannot work.
  const [email, setEmail] = useState(initial.email);
  useEffect(() => {
    if (!email && user && user.email) setEmail(normalise(user.email));
  }, [email, user]);

  // The password is asked for only when this tab was not handed one - after a reload, or on
  // arrival from a route guard. Verification sets the account's password to whatever is sent
  // with the code, so it is required either way; this only decides where it comes from.
  const [needsPassword, setNeedsPassword] = useState(() => {
    const remembered = getPendingPassword(initial.email);
    return !(remembered && remembered.length >= PASSWORD_MIN_LENGTH);
  });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [digits, setDigits] = useState(() => Array(CODE_LENGTH).fill(''));
  const inputsRef = useRef([]);

  const [arrival, setArrival] = useState(initial.arrival);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(() => initialNotice(initial.arrival, initial.expiresAt).error);
  const [info, setInfo] = useState(() => initialNotice(initial.arrival, initial.expiresAt).info);
  // Set when the code cannot work any more - expired, or too many wrong guesses. Verify is
  // disabled in that state, because the only thing that helps is a new code.
  const [dead, setDead] = useState('');

  // Absolute timestamps, carried in history state, so a refresh shows the time actually
  // left rather than restarting the clock. null expiry means there is no code we know of to
  // count down to.
  const [expiresAt, setExpiresAt] = useState(initial.expiresAt);
  const [resendReadyAt, setResendReadyAt] = useState(() => (
    // Only a code actually sent on the way here earns a cooldown. Every other arrival opens
    // with the button live: the cooldown exists to space out real emails, and making someone
    // wait for their first one is just a delay.
    initial.arrival === 'sent' && initial.sentAt !== null ? initial.sentAt + RESEND_COOLDOWN_MS : 0
  ));
  const [logoOk, setLogoOk] = useState(true);

  // One ticker drives both countdowns. A second of drift either way does not matter here,
  // and one interval is cheaper than two.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const msLeft = expiresAt === null ? null : expiresAt - now;
  const codeExpired = msLeft !== null && msLeft <= 0;
  const resendIn = resendReadyAt - now;
  const canResend = resendIn <= 0 && !submitting && !!email;

  // A verified user has no business on this page. Sending them to the app rather than
  // rendering the form covers the back button after a successful verification, and the
  // already-signed-in user who types the URL.
  useEffect(() => {
    if (loading) return;
    if (user && (user.email_confirmed_at || user.confirmed_at)) {
      navigate('/app', { replace: true });
    }
  }, [user, loading, navigate]);

  const focusBox = useCallback((index) => {
    const el = inputsRef.current[index];
    if (el) {
      el.focus();
      el.select();
    }
  }, []);

  function setDigitAt(index, value) {
    setDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    if (error) setError('');
  }

  function handleChange(index, raw) {
    // Strip anything that is not a digit first, so a stray letter or a space never lands in
    // a box. A multi-character value here is normal, not exceptional: phone keyboards and
    // iOS SMS-style autofill both deliver several characters into one input at once.
    const cleaned = String(raw).replace(/\D/g, '');
    if (!cleaned) {
      setDigitAt(index, '');
      return;
    }

    if (cleaned.length === 1) {
      setDigitAt(index, cleaned);
      if (index < CODE_LENGTH - 1) focusBox(index + 1);
      return;
    }

    fillFrom(index, cleaned);
  }

  // Spread a run of digits across the boxes from `start`, and land the cursor on the first
  // box still empty. Shared by autofill and paste.
  function fillFrom(start, digitString) {
    setDigits((prev) => {
      const next = [...prev];
      for (let i = 0; i < digitString.length && start + i < CODE_LENGTH; i++) {
        next[start + i] = digitString[i];
      }
      return next;
    });
    if (error) setError('');
    const landing = Math.min(start + digitString.length, CODE_LENGTH - 1);
    setTimeout(() => focusBox(landing), 0);
  }

  function handleKeyDown(index, e) {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      // Backspace in an empty box steps back and clears the previous one, which is what
      // every code input people have used before this one does.
      e.preventDefault();
      setDigitAt(index - 1, '');
      focusBox(index - 1);
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusBox(index - 1);
    }
    if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      e.preventDefault();
      focusBox(index + 1);
    }
  }

  function handlePaste(index, e) {
    const pasted = (e.clipboardData || window.clipboardData).getData('text');
    const cleaned = String(pasted || '').replace(/\D/g, '');
    if (!cleaned) return;
    // Pasting the whole code into the fourth box should still fill the code, not just the
    // boxes after it - most people paste into whichever box the cursor happens to be in.
    e.preventDefault();
    fillFrom(cleaned.length >= CODE_LENGTH ? 0 : index, cleaned.slice(0, CODE_LENGTH));
  }

  const code = digits.join('');
  const complete = code.length === CODE_LENGTH;

  /**
   * What happens after the backend says the address is verified.
   *
   * The backend revoked every session for this account as part of verifying it, so any
   * session this browser holds is already dead - including the one a user bounced here from
   * /app arrived with. It is dropped locally (no network call to a server that has already
   * forgotten it) and the user is signed in fresh with the password that was just set. If
   * that sign-in fails, the address is verified either way - that is the part that cannot
   * be redone - so the login page takes over with a note saying so.
   */
  async function completeSignIn(accountPassword) {
    if (supabase) {
      try {
        const { data } = await supabase.auth.getSession();
        if (data && data.session) await supabase.auth.signOut({ scope: 'local' });

        const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password: accountPassword });
        if (!signInErr) {
          clearPendingRegistration();
          navigate('/app', { replace: true });
          return;
        }
      } catch {
        // Fall through to the login page.
      }
    }

    clearPendingRegistration();
    navigate('/login', { replace: true, state: { verified: true, email } });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!complete || submitting || codeExpired || dead) return;

    let accountPassword = needsPassword ? password : getPendingPassword(email);
    if (!needsPassword && !accountPassword) {
      // The hand-off was cleared underneath us. Ask rather than fail.
      setNeedsPassword(true);
      setError('Confirm your password to finish setting up your account.');
      return;
    }
    if (needsPassword) {
      if (password.length < PASSWORD_MIN_LENGTH) {
        setError(`Your password needs at least ${PASSWORD_MIN_LENGTH} characters.`);
        return;
      }
      if (password !== confirmPassword) {
        setError("Those passwords don't match.");
        return;
      }
      accountPassword = password;
    }

    setError('');
    setInfo('');
    setSubmitting(true);
    const result = await verifyEmailCode(email, code, accountPassword);

    if (result.ok) {
      trackEvent('email_verified');
      await completeSignIn(accountPassword);
      return;
    }

    setSubmitting(false);
    setDigits(Array(CODE_LENGTH).fill(''));
    focusBox(0);

    if (result.expired) {
      setDead('expired');
      setExpiresAt(Date.now()); // stop the countdown at zero so the page agrees with itself
      setError(result.message);
      return;
    }
    if (result.exhausted) {
      setDead('exhausted');
      setError(result.message);
      return;
    }

    // A wrong code, and the one case where the user can simply try again. The remaining
    // attempts are shown when the backend reports them, so nobody discovers the ceiling by
    // hitting it.
    setError(
      typeof result.attemptsLeft === 'number'
        ? `${result.message} ${result.attemptsLeft} ${result.attemptsLeft === 1 ? 'attempt' : 'attempts'} left before you'll need a new code.`
        : result.message,
    );
  }

  async function handleResend() {
    if (!canResend) return;

    setError('');
    setInfo('');
    setSubmitting(true);
    const result = await resendVerificationCode(email);
    setSubmitting(false);

    // Start the cooldown whatever the answer. A failed send that leaves the button live
    // invites exactly the retry storm the per-address cap exists to absorb.
    const sentAt = Date.now();
    setResendReadyAt(sentAt + RESEND_COOLDOWN_MS);

    if (!result.ok) {
      setError(result.message);
      return;
    }

    const minutes = result.expiresInMinutes || FALLBACK_TTL_MINUTES;
    const nextExpiry = sentAt + minutes * 60 * 1000;
    setExpiresAt(nextExpiry);
    setArrival('sent');
    setDigits(Array(CODE_LENGTH).fill(''));
    setDead('');
    setInfo(result.message);
    focusBox(0);

    // Record the new code's clock in history state, so a refresh from here shows the right
    // countdown and cooldown instead of the ones from the page's first load.
    navigate(location.pathname, {
      replace: true,
      state: { ...initial.state, email, codeSent: true, throttled: false, sentAt, expiresAt: nextExpiry },
    });
  }

  // Leaving to register with a different address. An unverified session has to go first:
  // GuestRoute sends any such session back here, so without this the link would loop.
  async function startOver() {
    clearPendingRegistration();
    if (user) {
      try {
        await signOut();
      } catch {
        // A failed sign-out leaves the guard to redirect back here, which is recoverable.
      }
    }
    navigate('/register');
  }

  // No address to verify: a direct visit to /verify-email, or a refresh in a browser where
  // sessionStorage is unavailable. Offer the two things that actually help instead of a
  // form that cannot be submitted.
  if (!email) {
    return (
      <Shell logoOk={logoOk} setLogoOk={setLogoOk} navigate={navigate}>
        <p className={styles.tagline}>Verify your email</p>
        <div className={styles.error} role="alert">
          We don't know which address to verify. Register again to get a new code, or sign in
          if you've already verified your email.
        </div>
        <button className={styles.btn} type="button" onClick={startOver}>
          Go to registration
        </button>
        <p className={styles.toggle}>
          Already verified?{' '}
          <button className={styles.link} type="button" onClick={() => navigate('/login')}>Sign in</button>
        </p>
      </Shell>
    );
  }

  return (
    <Shell logoOk={logoOk} setLogoOk={setLogoOk} navigate={navigate}>
      <p className={styles.tagline}>Verify your email</p>

      <p className={styles.helper}>
        {arrival === 'sent' ? "We've sent a verification code to" : 'Enter the verification code sent to'}
        <br />
        <span className={own.address}>{email}</span>
      </p>
      {arrival === 'cold' && (
        <p className={styles.hint}>
          Don't have a code, or has yours expired? Request a new one below.
        </p>
      )}

      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <label className={styles.label} htmlFor="verify-code-0">
          Enter the 6-digit code
        </label>

        <div className={own.codeRow}>
          {digits.map((digit, i) => (
            <input
              key={i}
              id={`verify-code-${i}`}
              ref={(el) => { inputsRef.current[i] = el; }}
              className={`${own.codeInput} ${error ? own.codeInputError : ''}`}
              // inputMode over type="number": it brings up the digit keypad on a phone
              // without the desktop spinner, the scroll-wheel-changes-the-value behaviour,
              // or number inputs' habit of dropping a leading zero - and "012345" is a
              // perfectly ordinary code.
              type="text"
              inputMode="numeric"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              maxLength={CODE_LENGTH}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={(e) => handlePaste(i, e)}
              onFocus={(e) => e.target.select()}
              disabled={submitting}
              aria-label={`Digit ${i + 1} of ${CODE_LENGTH}`}
              autoFocus={i === 0}
            />
          ))}
        </div>

        <div className={own.meta}>
          {msLeft === null ? (
            // Nothing we know of to count down to. The empty span keeps the resend control
            // on the right, where it sits in every other state.
            <span />
          ) : (
            <span
              className={`${own.timer} ${codeExpired ? own.timerDead : (msLeft < 60_000 ? own.timerUrgent : '')}`}
              role="timer"
              aria-live="off"
            >
              {codeExpired ? 'Code expired' : `Expires in ${mmss(msLeft)}`}
            </span>
          )}
          <button type="button" className={own.resend} onClick={handleResend} disabled={!canResend}>
            {resendIn > 0 ? `Resend in ${mmss(resendIn)}` : 'Resend code'}
          </button>
        </div>

        {needsPassword && (
          <>
            <p className={styles.hint}>
              Confirm your password to finish setting up your account.
            </p>
            <label className={styles.label} htmlFor="verify-password">Password</label>
            <input
              id="verify-password"
              className={styles.input}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); if (error) setError(''); }}
              disabled={submitting}
              minLength={PASSWORD_MIN_LENGTH}
              required
            />
            <label className={styles.label} htmlFor="verify-password-confirm">Confirm password</label>
            <input
              id="verify-password-confirm"
              className={styles.input}
              type="password"
              autoComplete="current-password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); if (error) setError(''); }}
              disabled={submitting}
              minLength={PASSWORD_MIN_LENGTH}
              required
            />
          </>
        )}

        {/* The countdown reaching zero is its own message: the user has not done anything
            wrong, and the only thing that helps is a new code. */}
        {codeExpired && !error && (
          <div className={styles.error} role="alert">
            Your code has expired. Send yourself a new one to continue.
          </div>
        )}
        {error && <div className={styles.error} role="alert">{error}</div>}
        {info && <div className={styles.info} role="status">{info}</div>}

        <button
          className={styles.btn}
          type="submit"
          disabled={submitting || !complete || codeExpired || !!dead}
        >
          {submitting ? 'Verifying…' : 'Verify Email'}
        </button>
      </form>

      <p className={styles.toggle}>
        Wrong address?{' '}
        <button className={styles.link} type="button" onClick={startOver}>
          Register again
        </button>
      </p>
    </Shell>
  );
}

// The card chrome, identical to the other auth pages. Extracted only because this page has
// two very different bodies (no-address and the form) and they must not drift apart.
function Shell({ logoOk, setLogoOk, navigate, children }) {
  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <button className={styles.logo} type="button" onClick={() => navigate('/')} aria-label="RepBuddy home">
          {logoOk ? (
            <img
              className={styles.logoImg}
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="RepBuddy"
              onError={() => setLogoOk(false)}
            />
          ) : (
            <div className={styles.logoMark}>R</div>
          )}
          <span className={styles.logoName}>RepBuddy</span>
        </button>

        {children}

        <button className={styles.backHome} type="button" onClick={() => navigate('/')}>
          ⬅️ Back to home
        </button>
      </div>
    </div>
  );
}
